'use client'

import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAdd, MdDelete, MdEdit, MdInfoOutline, MdStorefront } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    createStoreGoogleTag,
    listStoreGoogleTags,
    listStoreGoogleTagStores,
    removeStoreGoogleTag,
    StoreGoogleTagItem,
    StoreGoogleTagStore,
    updateStoreGoogleTag,
} from '@/lib/api/marketing/google-ads-api'

type FormState = {
    name: string
    measurementId: string
    adsTagId: string
    adsPurchaseLabel: string
    adsensePublisherId: string
    storeConfigIds: number[]
}

const EMPTY_FORM: FormState = {
    name: '',
    measurementId: '',
    adsTagId: '',
    adsPurchaseLabel: '',
    adsensePublisherId: '',
    storeConfigIds: [],
}

function tagLabel(tag: StoreGoogleTagItem): string {
    return tag.name || [tag.measurementId, tag.adsTagId, tag.adsensePublisherId].filter(Boolean).join(' / ')
}

/**
 * Google Analytics 4 e tag do Google Ads nas lojas online. Cada configuração escolhe as
 * lojas; sem nenhuma, vale para todas (uma só assim). A da loja vale sobre a de todas.
 */
export default function StoreGoogleTagsCard() {
    const [tags, setTags] = useState<StoreGoogleTagItem[]>([])
    const [stores, setStores] = useState<StoreGoogleTagStore[]>([])
    const [loading, setLoading] = useState(true)
    // `null`: lista. `'new'`: criando. Número: editando aquela configuração.
    const [editing, setEditing] = useState<number | 'new' | null>(null)
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const [removing, setRemoving] = useState<StoreGoogleTagItem | null>(null)
    const [removeBusy, setRemoveBusy] = useState(false)

    const load = useCallback(async () => {
        const [tagsRes, storesRes] = await Promise.all([listStoreGoogleTags(), listStoreGoogleTagStores()])
        if (tagsRes.success) setTags(tagsRes.result ?? [])
        if (storesRes.success) setStores(storesRes.result ?? [])
        setLoading(false)
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const storeName = (id: number) => stores.find((s) => s.id === id)?.name ?? `Loja #${id}`
    const others = tags.filter((t) => t.id !== editing)
    const otherGlobal = others.find((t) => t.stores.length === 0)
    const takenBy = (storeId: number) => others.find((t) => t.stores.some((s) => s.storeConfigId === storeId))

    const openForm = (tag: StoreGoogleTagItem | null) => {
        setEditing(tag ? tag.id : 'new')
        setForm(
            tag
                ? {
                      name: tag.name ?? '',
                      measurementId: tag.measurementId ?? '',
                      adsTagId: tag.adsTagId ?? '',
                      adsPurchaseLabel: tag.adsPurchaseLabel ?? '',
                      adsensePublisherId: tag.adsensePublisherId ?? '',
                      storeConfigIds: tag.stores.map((s) => s.storeConfigId),
                  }
                : EMPTY_FORM,
        )
    }

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((current) => ({ ...current, [key]: value }))

    const blockedGlobal = form.storeConfigIds.length === 0 && !!otherGlobal
    const canSave =
        (form.measurementId.trim() || form.adsTagId.trim() || form.adsensePublisherId.trim()) && !blockedGlobal
    // O AdSense só aprova o domínio principal: loja no subdomínio da plataforma não rende.
    const adsenseStores = form.storeConfigIds.length
        ? stores.filter((s) => form.storeConfigIds.includes(s.id))
        : stores.filter((s) => !takenBy(s.id))
    const adsenseWithoutDomain = form.adsensePublisherId.trim() ? adsenseStores.filter((s) => !s.customDomain) : []

    const handleSave = async () => {
        setSaving(true)
        try {
            const data = {
                name: form.name.trim() || null,
                measurementId: form.measurementId.trim() || null,
                adsTagId: form.adsTagId.trim() || null,
                adsPurchaseLabel: form.adsPurchaseLabel.trim() || null,
                adsensePublisherId: form.adsensePublisherId.trim() || null,
                storeConfigIds: form.storeConfigIds,
            }
            const res = editing === 'new' ? await createStoreGoogleTag(data) : await updateStoreGoogleTag(editing as number, data)
            if (!res.success) {
                toast.error(res.error || 'Não foi possível salvar a configuração')
                return
            }
            toast.success('Configuração salva. A loja já carrega as tags.')
            setEditing(null)
            await load()
        } finally {
            setSaving(false)
        }
    }

    const handleRemove = async () => {
        if (!removing) return
        setRemoveBusy(true)
        try {
            const res = await removeStoreGoogleTag(removing.id)
            if (!res.success) {
                toast.error(res.error || 'Não foi possível remover a configuração')
                return
            }
            toast.success('Configuração removida')
            setRemoving(null)
            await load()
        } finally {
            setRemoveBusy(false)
        }
    }

    return (
        <div className="mt-6 bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="font-semibold text-base-content">Google Analytics, Google Ads e AdSense na loja online</h3>
                    <p className="text-sm text-base-content/60 mt-1">
                        A loja carrega o Google Analytics 4 e a tag do Google Ads e envia visita, produto visto,
                        carrinho, início do checkout e compra. Com o AdSense, os anúncios do Google entram nas seções
                        que você escolher no editor do tema. Não precisa da conta conectada acima.
                    </p>
                </div>
                {editing === null && stores.length > 0 && (
                    <button className="btn btn-secondary btn-sm gap-1" onClick={() => openForm(null)}>
                        <MdAdd className="text-lg" /> Nova configuração
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-6">
                    <span className="loading loading-spinner loading-md text-secondary" />
                </div>
            ) : stores.length === 0 ? (
                <p className="text-sm text-base-content/60">A organização ainda não tem loja online.</p>
            ) : editing !== null ? (
                <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">Nome (opcional)</span>
                            <input
                                className="input input-bordered w-full"
                                maxLength={80}
                                placeholder="Ex.: Loja principal"
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                            />
                            <span className="text-xs text-base-content/60">Para achar a configuração quando houver mais de uma.</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">ID do Google Analytics</span>
                            <input
                                className="input input-bordered w-full font-mono"
                                maxLength={30}
                                placeholder="G-XXXXXXXXXX"
                                value={form.measurementId}
                                onChange={(e) => set('measurementId', e.target.value.toUpperCase())}
                            />
                            <span className="text-xs text-base-content/60">
                                No GA4: Administrador, Fluxos de dados, o fluxo da Web, ID da métrica.
                            </span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">ID da tag do Google Ads</span>
                            <input
                                className="input input-bordered w-full font-mono"
                                maxLength={30}
                                placeholder="AW-123456789"
                                value={form.adsTagId}
                                onChange={(e) => set('adsTagId', e.target.value.toUpperCase())}
                            />
                            <span className="text-xs text-base-content/60">
                                No Google Ads: Metas, Conversões, a ação de compra, Configuração da tag. Sozinho, alimenta o
                                remarketing.
                            </span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">Rótulo da conversão de compra (opcional)</span>
                            <input
                                className="input input-bordered w-full font-mono"
                                maxLength={100}
                                placeholder="AW-123456789/AbC-D_efG"
                                value={form.adsPurchaseLabel}
                                onChange={(e) => set('adsPurchaseLabel', e.target.value)}
                            />
                            <span className="text-xs text-base-content/60">
                                Cole o send_to da ação de compra inteiro ou só o trecho depois da barra. Preenchido, cada
                                pedido vira conversão, com valor e número do pedido.
                            </span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">ID de editor do AdSense (opcional)</span>
                            <input
                                className="input input-bordered w-full font-mono"
                                maxLength={40}
                                placeholder="ca-pub-1234567890123456"
                                value={form.adsensePublisherId}
                                onChange={(e) => set('adsensePublisherId', e.target.value.toLowerCase())}
                            />
                            <span className="text-xs text-base-content/60">
                                Para quem é parceiro do AdSense e quer anúncios do Google na loja. Os anúncios entram pela
                                seção "Anúncio do Google" no editor do tema.
                            </span>
                        </div>
                    </div>

                    {adsenseWithoutDomain.length > 0 && (
                        <div className="alert alert-warning text-sm">
                            <MdInfoOutline className="text-lg" />
                            <span>
                                O AdSense só aprova o domínio próprio da loja. Sem ele, os anúncios não aparecem em:{' '}
                                {adsenseWithoutDomain.map((s) => s.name).join(', ')}.
                            </span>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium">Aplicar exclusivamente em uma ou mais lojas</span>
                        <span className="text-xs text-base-content/60">
                            Opcional. Sem nenhuma marcada, vale para todas as lojas da organização. Marcada, a loja usa
                            esta configuração no lugar da que vale para todas.
                        </span>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {stores.map((store) => {
                                const checked = form.storeConfigIds.includes(store.id)
                                const other = takenBy(store.id)
                                return (
                                    <label
                                        key={store.id}
                                        className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                                            other
                                                ? 'cursor-not-allowed border-base-300 opacity-60'
                                                : checked
                                                  ? 'cursor-pointer border-secondary bg-secondary/5'
                                                  : 'cursor-pointer border-base-300 hover:border-secondary/50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={checked}
                                            disabled={!!other}
                                            onChange={(e) =>
                                                set(
                                                    'storeConfigIds',
                                                    e.target.checked
                                                        ? [...form.storeConfigIds, store.id]
                                                        : form.storeConfigIds.filter((id) => id !== store.id),
                                                )
                                            }
                                        />
                                        {store.logo ? (
                                            <img src={store.logo} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                                        ) : (
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-base-200">
                                                <MdStorefront className="text-base-content/30" />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium">{store.name}</p>
                                            <p className="truncate text-xs text-base-content/50">
                                                {other ? `Já está em: ${tagLabel(other)}` : store.customDomain || store.subdomain}
                                            </p>
                                        </div>
                                    </label>
                                )
                            })}
                        </div>
                    </div>

                    {blockedGlobal && (
                        <div className="alert alert-warning text-sm">
                            <MdInfoOutline className="text-lg" />
                            <span>
                                Já existe uma configuração para todas as lojas ({tagLabel(otherGlobal!)}). Marque as lojas
                                desta, ou remova a outra.
                            </span>
                        </div>
                    )}

                    {form.adsTagId.trim() && (
                        <div className="rounded-xl bg-base-200/50 p-4 text-sm text-base-content/70">
                            A compra é contada quando o pedido é feito, antes da confirmação do pagamento. Quando o envio da
                            cobrança paga ao Google Ads entrar (importação de cliques), deixe só uma das duas ações como
                            principal no Google Ads, para a venda não contar duas vezes.
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleSave} disabled={saving || !canSave}>
                            {saving && <span className="loading loading-spinner loading-xs" />} Salvar
                        </button>
                    </div>
                </div>
            ) : tags.length === 0 ? (
                <p className="text-sm text-base-content/60">Nenhuma configuração ainda.</p>
            ) : (
                <ul className="divide-y divide-base-200 rounded-xl border border-base-300/60">
                    {tags.map((tag) => (
                        <li key={tag.id} className="flex flex-wrap items-center gap-3 p-4">
                            <div className="min-w-0 flex-1 space-y-1">
                                <p className="font-medium text-base-content">{tagLabel(tag)}</p>
                                <div className="flex flex-wrap gap-1.5 text-xs">
                                    {tag.measurementId && <span className="badge badge-ghost font-mono">{tag.measurementId}</span>}
                                    {tag.adsTagId && <span className="badge badge-ghost font-mono">{tag.adsTagId}</span>}
                                    {tag.adsPurchaseLabel && <span className="badge badge-ghost">conversão de compra</span>}
                                    {tag.adsensePublisherId && (
                                        <span className="badge badge-ghost font-mono">{tag.adsensePublisherId}</span>
                                    )}
                                </div>
                                <p className="text-xs text-base-content/60">
                                    {tag.stores.length === 0
                                        ? 'Todas as lojas (exceto as que têm configuração própria)'
                                        : tag.stores.map((s) => storeName(s.storeConfigId)).join(', ')}
                                </p>
                            </div>
                            <button className="btn btn-ghost btn-sm gap-1" onClick={() => openForm(tag)}>
                                <MdEdit className="text-lg" /> Editar
                            </button>
                            <button className="btn btn-ghost btn-sm text-error gap-1" onClick={() => setRemoving(tag)}>
                                <MdDelete className="text-lg" /> Remover
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <ConfirmModal
                isOpen={!!removing}
                onClose={() => setRemoving(null)}
                onConfirm={handleRemove}
                title="Remover configuração"
                message={`As lojas desta configuração deixam de carregar ${removing ? tagLabel(removing) : ''} (as que não têm outra passam a usar a de todas as lojas, se houver). Deseja continuar?`}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                loading={removeBusy}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
